import { Dialog, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'
import baseURL from '../../../configs/api';
import CustomerSelectWrapper from './CustomerSelectWrapper';
import { post_Customer, put_Customer } from '../../../services/customer';
import { useTheme } from '../../../components/hooks/useTheme';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import colorConfigs from '../../../configs/colorConfigs';
import EmailAlreadyExistChecking from '../../../components/EmailAlreadyExistChecking';
import { getCustomerEmailChecking } from '../../../services/EmailChecking';
import useAuth from '../../../components/hooks/useAuth';
import { branchTimezone, getTheTotalDiscountAmount } from '../../../utils/summaryCalculation';
import { confirm_Quote } from '../../../services/quotes';
import { sendQuoteEmailAPI } from '../../../services/quoteEmail';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';
import moment from 'moment';
import useGeneral from '../../../components/hooks/useGeneral';

type Props = {
    sendEmailPopUp: boolean;
    hideSendEmailPopup: () => void;
    branchId: number;
    vehicleClassId: number;
    pickupDate: string;
    pickupTime: string;
    returnDate: string;
    returnTime: string;
    pickupLocation: number;
    returnLocation: number;
    securityDepositAmount: number;
    isCreatingPdf: boolean;
    checkExtraValue: any[];
    appliedDiscountList: any[];
    formattedCreatedAt: string;
    summaryData: any;
    setPleaseWaitPopup: (value: boolean) => void;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendQuotes = ({
    sendEmailPopUp,
    hideSendEmailPopup,
    branchId,
    vehicleClassId,
    pickupDate,
    pickupTime,
    returnDate,
    returnTime,
    pickupLocation,
    returnLocation,
    securityDepositAmount,
    isCreatingPdf,
    checkExtraValue,
    appliedDiscountList,
    formattedCreatedAt,
    summaryData,
    setPleaseWaitPopup
}: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const [customers, setCustomers] = useState<any[]>([]);
    const [customerId, setCustomerId] = useState<string>("");
    const [pageNo, setPage] = useState<number>(-1);
    const [hasNextPage, setHasNextPage] = useState<boolean>(true);
    const [isNextPageLoading, setNextPageLoading] = useState<boolean>(false);
    const [options, setOptions] = useState([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<any>();
    const [tags, setTags] = useState<string>("");
    const [cc, setCc] = useState<string>("");
    const [language, setLanguage] = useState<string>("");
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [addAndEditCustomerPopup, setAddAndEditCustomerPopup] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [error, setError] = useState('');
    const [emptyFieldAlert, setEmptyFieldAlert] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [discountTotalAmount, setDiscountTotalAmount] = useState<number>(0.0);

    const UpdateTimezone = branchTimezone(formattedCreatedAt);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    let latestPickup = new Date();
    let latestReturn = new Date();
    if (pickupDate) latestPickup = new Date(pickupTime ? `${pickupDate}T${pickupTime}` : pickupDate);
    if (returnDate) latestReturn = new Date(returnTime ? `${returnDate}T${returnTime}` : returnDate);

    useEffect(() => {
        if (isChanged && options?.length !== 0 && customers?.length !== 0) {
            setOptions([]);
            setCustomers([]);
        } else if (isChanged && options?.length === 0 && customers?.length === 0) {
            loadWithOutSearchOptions(0);
            setIsChanged(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChanged, options, customers])

    useEffect(() => {
        if (appliedDiscountList?.length > 0) {
            let discountAmount = getTheTotalDiscountAmount(appliedDiscountList);
            setDiscountTotalAmount(discountAmount);
        } else {
            setDiscountTotalAmount(0);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedDiscountList]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCc(value);

        // Clear error as user types
        if (error) setError('');
    };

    const validateEmail = () => {
        if (cc?.trim() !== "" && !emailRegex.test(cc)) {
            setError('Please enter a valid email address');
        } else {
            setError('');
        }
    };

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;
        setHasNextPage(true);
        setNextPageLoading(false);
        setPage(-1);
        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                setIsSearching(false);
                setOptions([]);
                setCustomers([]);
            }, 1000);
        } else {
            setOptions([]);
            setCustomers([]);
            setIsSearching(false);
        }

        return () => clearTimeout(timeOutId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const loadNextPage = () => {

        if (searchValue === "") {
            loadWithOutSearchOptions(pageNo + 1);
        } else {
            loadWithSearchOptions(pageNo + 1);
        }
    };

    const loadWithOutSearchOptions = (page: number) => {
        try {
            const pagination = {
                page: page,
                size: 15,
            };
            setNextPageLoading(true);
            fetch(`${baseURL}Customer/GetCustomersPaging/${pagination.page},${pagination.size}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const dataOptions = data.customers.map((item: any) => {
                        return {
                            value: item.id,
                            label: item.firstName + " " + item.lastName,
                        };
                    });

                    const itemsData = options.concat(dataOptions);
                    setOptions(itemsData);
                    setCustomers([...customers, ...data?.customers]);
                    setNextPageLoading(false);
                    setHasNextPage(itemsData.length < data.count);
                    setPage(page);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                });
        } catch (error) {
            handleTenantError(error);
        }
    };

    const loadWithSearchOptions = (page: number) => {
        try {
            const pagination = {
                page: page,
                size: 15,
            };
            setNextPageLoading(true);
            fetch(`${baseURL}Customer/GetCustomersSearchWithCustomerNamePaging?customerName=${searchValue}&page=${pagination.page}&size=${pagination.size}`, {
                method: "GET",
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "application/json"
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    const dataOptions = data.customers.map((item: any) => {
                        return {
                            value: item.id,
                            label: item.firstName + " " + item.lastName,
                        };
                    });

                    const itemsData = options.concat(dataOptions);
                    setOptions(itemsData);
                    setCustomers([...customers, ...data?.customers]);
                    setNextPageLoading(false);
                    setHasNextPage(itemsData.length < data.count);
                    setPage(page);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                });
        } catch (error) {
            handleTenantError(error);
        }
    };

    const addCustomer = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhoneNumber("");
        setIsEditing(false);
        setAddAndEditCustomerPopup(true);
    }

    const editCustomer = () => {
        const selectedCustomer: any = customers?.find((item: any) => item?.id === parseInt(customerId));
        if (selectedCustomer) {
            setFirstName(selectedCustomer?.firstName ? selectedCustomer?.firstName : "");
            setLastName(selectedCustomer?.lastName ? selectedCustomer?.lastName : "");
            setEmail(selectedCustomer?.emailAddress ? selectedCustomer?.emailAddress : "");
            setPhoneNumber(selectedCustomer?.phoneNumber ? selectedCustomer?.phoneNumber : "");
        } else {
            setFirstName("");
            setLastName("");
            setEmail("");
            setPhoneNumber("");
        }
        setIsEditing(true);
        setAddAndEditCustomerPopup(true);
    }

    const handleTheQuote = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        confrimTheQuote();
    }

    const handleCustomerSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (firstName && lastName && email && phoneNumber) {
            setEmptyFieldAlert(false);
            setPleaseWaitPopup(true);
            const selectedCustomer: any = customers?.find((item: any) => item?.id === parseInt(customerId));

            if (isEditing && selectedCustomer) {
                await put_Customer({
                    ...selectedCustomer,
                    firstName,
                    lastName,
                    emailAddress: email,
                    phoneNumber: phoneNumber
                }, auth?.id, tenantId)
                    .then(() => {
                        setSelectedOption({
                            value: customerId,
                            label: firstName + " " + lastName
                        })
                        setIsChanged(true);
                        setPleaseWaitPopup(false);
                        setAddAndEditCustomerPopup(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setPleaseWaitPopup(false);
                    });
            } else {
                await post_Customer({
                    firstName,
                    lastName,
                    emailAddress: email,
                    phoneNumber: phoneNumber,
                }, auth?.id, tenantId)
                    .then((response) => {
                        setIsChanged(true);
                        setSelectedOption({
                            value: response?.id,
                            label: response?.firstName + " " + response?.lastName
                        })
                        setCustomerId(response?.id.toString());
                        setPleaseWaitPopup(false);
                        setAddAndEditCustomerPopup(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setPleaseWaitPopup(false);
                    });
            }
        }
        else {
            setEmptyFieldAlert(true);
        }
    }

    const confrimTheQuote = async () => {
        if (!error) {
            setPleaseWaitPopup(true);

            let additionalValue = await Promise.all(
                summaryData?.additionalCategoryDto?.map(async (item: any) => {
                    return item?.additionalChargesDto;
                }) || []
            );
            additionalValue = additionalValue.flat();

            await confirm_Quote({
                quote: {
                    reservationType: "Reservations",
                    customerId: parseInt(customerId),
                    vehicleClassId: vehicleClassId,
                    branchId: branchId,
                    pickupLoationId: pickupLocation,
                    returenLoationId: returnLocation,
                    pickupDate: `${moment(latestPickup).format(dateFormat)} ${moment(latestPickup).format("hh:mm:ss a")}`,
                    returenDate: `${moment(latestReturn).format(dateFormat)} ${moment(latestReturn).format("hh:mm:ss a")}`,
                    securityDepositAmount: summaryData?.securityDepositAmount,
                    hourlyRate: summaryData?.hourlyRate || 0,
                    dailyRate: summaryData?.dailyRate || 0,
                    weeklyRate: summaryData?.weeklyRate || 0,
                    monthlyRate: summaryData?.monthlyRate || 0,
                    commissionPartner: "",
                    clientPaysatCommissionPartner: false,
                    rackPrice: summaryData?.totalBasePriceBeforeDiscount,
                    totalPrice: summaryData?.totalPrices,
                    discount: discountTotalAmount,
                    status: isCreatingPdf ? "Create Pdf" : "Send Email",
                    tag: tags,
                    locationFeeAmount: summaryData?.locationFeeAmount ?? 0,
                    taxAmount: summaryData?.taxAmount ?? 0,
                    mandatoryAdditionalChargesAmount: summaryData?.mandatoryAdditionalChargesAmount ?? 0,
                    governmentTaxAmount: summaryData?.governmentTaxAmount ?? 0,
                    locationFeePercentage: summaryData?.locationFeePercentage ?? 0,
                    taxPercentage: summaryData?.taxPercentage ?? 0,
                    governmentTaxPercentage: summaryData?.governmentTaxPercentage ?? 0,
                    createdDate: new Date().toLocaleString(),
                    reservationId: 0
                },
                quoteAdditionalCharges: additionalValue?.map(item => (
                    {
                        additionalChargeByBranchId: item?.additionalChargeByBranchId,
                        quantity: item?.quantity,
                        basePrice: item?.basePrice
                    }
                )),
                quoteDiscounts: appliedDiscountList
                    ?.filter((item: any) => item?.discountApplied)
                    ?.map(item =>
                    ({
                        discountId: item?.discountId,
                        discountDefinition: item?.discountDefinition,
                        discountType: item?.discountType,
                        discountAmount: item?.discountAmount,
                        manualDiscount: item?.manualDiscount,
                        supportingDocuments: false,
                        dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                        createdAt: new Date(UpdateTimezone).toLocaleString(),
                        createdBy: auth?.givenname
                    })
                    ),
                fileUploadDatas: []
            }, auth?.id, tenantId)
                .then((response) => {
                    if (isCreatingPdf) {
                        hideSendEmailPopup();
                        window.open(`/carrental/reservations/print-quote?id=${response?.id}`, "_blank", "noopener,noreferrer");
                    } else {
                        sendQuoteEmail(response?.id);
                    }

                    setPleaseWaitPopup(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWaitPopup(false);
                    handleTenantError(error);
                });
        }
    }

    const sendQuoteEmail = async (quoteId: number) => {
        await sendQuoteEmailAPI(quoteId, cc, auth?.id, tenantId)
            .then(() => {
                setPleaseWaitPopup(false);
                hideSendEmailPopup();
                handleTenantError(null);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                hideSendEmailPopup();
                handleTenantError(error);
            });
    }


    return (
        <>
            <Dialog
                open={sendEmailPopUp}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => hideSendEmailPopup()}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <form onSubmit={handleTheQuote} className="mt-2 p-2">
                                    <div className="w-full">
                                        <div className="flex flex-col gap-y-[0.5rem]">
                                            <div>
                                                <div className={`flex justify-between items-center border-b mb-[0.75rem] ${themeStyles.main.borderColor}`}>
                                                    <div className="flex items-center gap-1.5">
                                                        <h2 className={`text-sm font-semibold ${themeStyles.main.textHeadingColor}`}>{isCreatingPdf ? "Create Pdf of Quote" : "Send Quote to email"}</h2>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Customer</label>
                                                            <CustomerSelectWrapper
                                                                hasNextPage={hasNextPage}
                                                                isNextPageLoading={isNextPageLoading}
                                                                setSearchValue={setSearchValue}
                                                                options={options}
                                                                setPage={setPage}
                                                                loadNextPage={loadNextPage}
                                                                selectedOption={selectedOption}
                                                                isSearching={isSearching}
                                                                setIsSearching={setIsSearching}
                                                                onChange={(selectedOption: any) => {
                                                                    setCustomerId(selectedOption?.value)
                                                                    setSelectedOption(selectedOption);
                                                                }}
                                                                baseWidth="100%"
                                                                height={150}
                                                            />

                                                            <div className="flex items-center gap-x-[0.75rem]">
                                                                <p className="text-xs text-[#167AC3] py-2 hover:underline cursor-pointer font-medium group-hover:font-bold group-hover:scale-150" onClick={addCustomer}>
                                                                    Add Contact
                                                                </p>

                                                                {
                                                                    customerId && (
                                                                        <p
                                                                            className="text-xs text-[#167AC3] py-2 hover:underline cursor-pointer font-medium group-hover:font-bold group-hover:scale-150"
                                                                            onClick={editCustomer}
                                                                        >
                                                                            Edit Contact
                                                                        </p>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Cc</label>
                                                            <input
                                                                type="email"
                                                                className={`w-full px-[8px] py-[8px] border rounded-md ${error ? "border-red-500" : themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                value={cc}
                                                                onChange={handleChange}
                                                                onBlur={validateEmail}
                                                            />
                                                            <br />
                                                            {error && <span className="text-red-500 text-sm">{error}</span>}
                                                        </div>

                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Language</label>
                                                            <select
                                                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                value={language}
                                                                onChange={(e) => setLanguage(e.target.value)}
                                                            >
                                                                <option className={`${themeStyles.main.tailwindSectionBg}`} value={"English"}>English</option>
                                                            </select>
                                                        </div>

                                                        <div />
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Tags</label>
                                                            <input
                                                                type="text"
                                                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                value={tags}
                                                                onChange={(e) => setTags(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 mt-6">
                                                <Button
                                                    variant="contained"
                                                    type="submit"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                >
                                                    {isCreatingPdf ? "Create Pdf" : "Send Quote"}
                                                </Button>

                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => hideSendEmailPopup()}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={addAndEditCustomerPopup}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => setAddAndEditCustomerPopup(false)}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div className="flex flex-col gap-y-[0.5rem]">
                                            <form onSubmit={handleCustomerSubmit}>
                                                <div>
                                                    <div className={`flex justify-between items-center border-b mb-[0.75rem] ${themeStyles.main.borderColor}`}>
                                                        <div className="flex items-center gap-1.5">
                                                            <h2 className={`text-sm font-semibold ${themeStyles.main.textHeadingColor}`}>{isEditing ? "Editing " : "Create "}Contact</h2>
                                                        </div>
                                                    </div>

                                                    {
                                                        emptyFieldAlert && (
                                                            <span className="text-xs text-red-500 inline-block my-2">All fields are mandatory!</span>
                                                        )
                                                    }

                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>First Name</label>
                                                                <input
                                                                    type="text"
                                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                    value={firstName}
                                                                    onChange={(e) => setFirstName(e.target.value)}
                                                                />
                                                            </div>

                                                            <div>
                                                                <EmailAlreadyExistChecking
                                                                    emailAddress={email}
                                                                    setEmailAddress={setEmail}
                                                                    getEmailChecking={getCustomerEmailChecking}
                                                                    className={{
                                                                        label: {
                                                                            fontSize: "12px",
                                                                            fontWeight: 500,
                                                                            color: themeStyles.form.labelTextColor
                                                                        },
                                                                        input: {
                                                                            width: "100%",
                                                                            fontSize: "14px",
                                                                            borderRadius: "6px",
                                                                            outline: "none",
                                                                            color: themeStyles.form.inputTextColor
                                                                        },
                                                                        successMessage: {
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            marginTop: '0.25rem',
                                                                            fontSize: '0.75rem',
                                                                            color: '#00b300',
                                                                        },
                                                                        errorMessage: {
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            marginTop: '0.25rem',
                                                                            fontSize: '0.75rem',
                                                                            color: '#ff3333',
                                                                        },
                                                                    }}
                                                                    touched={emailTouched}
                                                                    setTouched={setEmailTouched}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Last Name</label>
                                                                <input
                                                                    type="text"
                                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                    value={lastName}
                                                                    onChange={(e) => setLastName(e.target.value)}
                                                                />
                                                            </div>

                                                            <div>
                                                                <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Phone Number</label>
                                                                <PhoneNumberInput
                                                                    name={"phoneNumber"}
                                                                    value={phoneNumber}
                                                                    setValue={setPhoneNumber}
                                                                    fontSize={"14px"}
                                                                    paddingBlock={"5px"}
                                                                    required={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4 mt-6">
                                                    <Button variant="contained"
                                                        sx={{
                                                            backgroundColor: colorConfigs.btn.main.bg
                                                        }}
                                                        type="submit"
                                                    >
                                                        Save
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: colorConfigs.btn.cancel.bg,
                                                            border: colorConfigs.btn.cancel.border,
                                                            color: colorConfigs.btn.cancel.color
                                                        }}
                                                        onClick={() => setAddAndEditCustomerPopup(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default SendQuotes